export class EncryptionHelper {
  /**
   * Derive AES-256 key using PBKDF2 (Web Crypto API)
   * Salt = SHA256(merchantCode)
   */
  static async createAesKey(
    merchantKey: string,
    merchantCode: string
  ): Promise<ArrayBuffer> {
    if (!merchantKey || !merchantCode) {
      throw new Error('merchantKey and merchantCode are required');
    }

    // Password is merchantKey (as UTF-8 bytes)
    const password = new TextEncoder().encode(merchantKey);

    // Import password for PBKDF2
    const importedKey = await window.crypto.subtle.importKey(
      'raw',
      password,
      { name: 'PBKDF2' },
      false,
      ['deriveBits']
    );

    // Deterministic salt: SHA-256(merchantCode)
    const saltBuffer = new TextEncoder().encode(merchantCode);
    const salt = await window.crypto.subtle.digest('SHA-256', saltBuffer);

    // Derive 32 bytes (256 bits) key material using PBKDF2
    // 310,000 iterations, SHA-256
    const keyBits = await window.crypto.subtle.deriveBits(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: 310000,
        hash: 'SHA-256',
      },
      importedKey,
      256 // 256 bits = 32 bytes
    );

    return keyBits;
  }

  /**
   * Generate a random 12-byte nonce (IV) for AES-GCM
   */
  static generateNonce(): Uint8Array {
    return window.crypto.getRandomValues(new Uint8Array(12));
  }

  /**
   * Encrypt plaintext using AES-256-GCM
   * Returns base64-encoded: nonce + ciphertext + authTag
   */
  static async encrypt(
    plainText: string,
    merchantKey: string,
    merchantCode: string
  ): Promise<string> {
    if (!plainText || plainText.trim().length === 0) {
      throw new Error('plainText is required');
    }

    // Get raw key bytes
    const keyBytes = await this.createAesKey(merchantKey, merchantCode);

    // Import as AES-GCM key
    const key = await window.crypto.subtle.importKey(
      'raw',
      keyBytes,
      { name: 'AES-GCM' },
      false,
      ['encrypt']
    );

    const nonce = this.generateNonce();
    const plainTextBytes = new TextEncoder().encode(plainText);

    // Encrypt using AES-GCM
    // Web Crypto API's encrypt returns ciphertext + tag concatenated
    const encrypted = await window.crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: nonce,
        tagLength: 128, // 16 bytes auth tag (128 bits)
      },
      key,
      plainTextBytes
    );

    const encryptedArray = new Uint8Array(encrypted);

    // Split encrypted data: last 16 bytes are tag, rest is ciphertext
    const ciphertext = encryptedArray.slice(0, encryptedArray.length - 16);
    const tag = encryptedArray.slice(encryptedArray.length - 16);

    // Combine in format: nonce + ciphertext + tag
    const result = new Uint8Array(nonce.length + ciphertext.length + tag.length);
    result.set(nonce, 0);                                          // First 12 bytes: nonce
    result.set(ciphertext, nonce.length);                          // Middle: ciphertext
    result.set(tag, nonce.length + ciphertext.length);             // Last 16 bytes: tag

    // Convert to base64
    return this.arrayBufferToBase64(result);
  }

  /**
   * Decrypt base64-encoded ciphertext using AES-256-GCM
   * Expects format: nonce + ciphertext + authTag
   */
  static async decrypt(
    base64Cipher: string,
    merchantKey: string,
    merchantCode: string
  ): Promise<string> {
    if (!base64Cipher || base64Cipher.trim().length === 0) {
      throw new Error('cipherText is required');
    }

    const data = this.base64ToArrayBuffer(base64Cipher);

    // Minimum: 12 bytes nonce + 16 bytes tag + at least 1 byte ciphertext
    if (data.length < 12 + 16 + 1) {
      throw new Error('Invalid ciphertext length or insufficient data');
    }

    // Get raw key bytes
    const keyBytes = await this.createAesKey(merchantKey, merchantCode);

    // Import as AES-GCM key
    const key = await window.crypto.subtle.importKey(
      'raw',
      keyBytes,
      { name: 'AES-GCM' },
      false,
      ['decrypt']
    );

    // Extract components: nonce + ciphertext + tag
    const nonce = data.slice(0, 12);                              // First 12 bytes
    const ciphertext = data.slice(12, data.length - 16);          // Middle bytes
    const tag = data.slice(data.length - 16);                     // Last 16 bytes

    // Web Crypto API expects ciphertext + tag concatenated
    const ciphertextWithTag = new Uint8Array(ciphertext.length + tag.length);
    ciphertextWithTag.set(ciphertext, 0);
    ciphertextWithTag.set(tag, ciphertext.length);

    try {
      // Decrypt using AES-GCM
      const decrypted = await window.crypto.subtle.decrypt(
        {
          name: 'AES-GCM',
          iv: nonce,
          tagLength: 128,
        },
        key,
        ciphertextWithTag
      );

      // Convert decrypted bytes to string
      return new TextDecoder().decode(decrypted);
    } catch (err) {
      throw new Error(
        'Decryption failed: Authentication check failed (data tampered or key/nonce/tag invalid).'
      );
    }
  }

  /**
   * Helper: Convert Uint8Array to base64 string
   */
  private static arrayBufferToBase64(buffer: Uint8Array): string {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  /**
   * Helper: Convert base64 string to Uint8Array
   */
  private static base64ToArrayBuffer(base64: string): Uint8Array {
    const binary = window.atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }
}